import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Lock, ShieldCheck, FileText, ArrowRight } from "lucide-react";
import { toast } from "sonner";

// Shared with JoinEstate.jsx — kept as one string so the two account-creation pages can't
// silently drift once the linked pages stop being drafts.
export const CONSENT_NOTICE = (
    <>
        By continuing, you agree to our{" "}
        <Link to="/terms" className="underline" target="_blank" rel="noopener noreferrer">terms</Link>{" "}
        and{" "}
        <Link to="/privacy-notice" className="underline" target="_blank" rel="noopener noreferrer">privacy notice</Link>{" "}
        — both still drafts.
    </>
);

/**
 * Self-serve signup — the first resident, before any estate exists.
 *
 * The page has one job beyond collecting four fields: make the privacy default legible
 * before the person commits. A workspace created here holds only their own documents. It
 * cannot invite anyone or share anything estate-wide until a director of the management
 * company claims it, because the management company — not an individual resident — is the
 * data controller (DOMAIN-UK-ESTATE-MANAGEMENT.md §7.1). Saying so here is cheaper than
 * explaining it after they have uploaded a deed.
 */
export default function SignUp() {
    const navigate = useNavigate();
    const { signup } = useAuth();
    const [form, setForm] = useState({
        full_name: "",
        email: "",
        password: "",
        estate_name: "",
    });
    const [submitting, setSubmitting] = useState(false);

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        if (form.password.length < 8) {
            toast.error("Password must be at least 8 characters");
            return;
        }
        setSubmitting(true);
        try {
            await signup(form);
            toast.success("Your private workspace is ready");
            navigate("/onboarding");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not create your workspace");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen grid lg:grid-cols-2 bg-[#F9FAFB]">
            {/* Left: what this is, and who it is for. */}
            <div className="hidden lg:flex flex-col justify-center px-14 py-16 bg-slate-900 text-slate-100">
                <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-slate-400">
                    <ShieldCheck size={14} /> For residents and their management company
                </div>
                <h1 className="font-heading text-4xl font-bold mt-4 leading-tight">
                    Find out where your estate charges actually go.
                </h1>
                <p className="text-slate-300 mt-5 max-w-md leading-relaxed">
                    Upload your service charge statement and see it broken down line by line, with every
                    figure traced back to the document it came from.
                </p>
                <ul className="mt-8 space-y-4 text-sm text-slate-300 max-w-md">
                    <li className="flex gap-3">
                        <Lock size={18} className="text-slate-400 shrink-0 mt-0.5" />
                        <span>
                            <strong className="text-white">Private by default.</strong> Your documents are
                            yours alone. Nothing is shared with anyone until you say so.
                        </span>
                    </li>
                    <li className="flex gap-3">
                        <FileText size={18} className="text-slate-400 shrink-0 mt-0.5" />
                        <span>
                            <strong className="text-white">Evidence, not opinions.</strong> Every number
                            links to its source document. Nothing is asserted that can't be shown.
                        </span>
                    </li>
                    <li className="flex gap-3">
                        <ShieldCheck size={18} className="text-slate-400 shrink-0 mt-0.5" />
                        <span>
                            <strong className="text-white">Free to start.</strong> Work on your own
                            documents at no cost. You only pay when your estate takes it on together.
                        </span>
                    </li>
                </ul>
            </div>

            {/* Right: the form. */}
            <div className="flex items-center justify-center px-6 py-12">
                <div className="w-full max-w-md" data-testid="signup-panel">
                    <h2 className="font-heading text-2xl font-bold text-slate-900">
                        Create your private workspace
                    </h2>
                    <p className="text-sm text-slate-600 mt-2">
                        Free, and it stays private to you. No invitation needed.
                    </p>

                    <form onSubmit={submit} className="mt-8 space-y-4">
                        <div>
                            <Label htmlFor="full_name">Your name</Label>
                            <Input
                                id="full_name"
                                value={form.full_name}
                                onChange={set("full_name")}
                                required
                                placeholder="Dana Okafor"
                                data-testid="signup-name-input"
                            />
                        </div>
                        <div>
                            <Label htmlFor="email">Email</Label>
                            <Input
                                id="email"
                                type="email"
                                value={form.email}
                                onChange={set("email")}
                                required
                                placeholder="you@example.com"
                                data-testid="signup-email-input"
                            />
                        </div>
                        <div>
                            <Label htmlFor="password">Password</Label>
                            <Input
                                id="password"
                                type="password"
                                value={form.password}
                                onChange={set("password")}
                                required
                                minLength={8}
                                placeholder="At least 8 characters"
                                data-testid="signup-password-input"
                            />
                        </div>
                        <div>
                            <Label htmlFor="estate_name">What is your estate called?</Label>
                            <Input
                                id="estate_name"
                                value={form.estate_name}
                                onChange={set("estate_name")}
                                required
                                placeholder="Richardson Close"
                                data-testid="signup-estate-input"
                            />
                            <p className="text-xs text-slate-500 mt-1.5">
                                Just so your workspace has a name. Your neighbours aren't notified.
                            </p>
                        </div>

                        <div className="rounded-md bg-slate-50 border border-slate-200 p-3.5 flex gap-2.5">
                            <Lock size={15} className="text-slate-500 shrink-0 mt-0.5" />
                            <p className="text-xs text-slate-600 leading-relaxed">
                                This workspace holds <strong>only your own documents</strong>. Inviting
                                neighbours needs a director of your management company to take it on first —
                                they're responsible for residents' data, so they have to be the one to ask.
                            </p>
                        </div>

                        <Button
                            type="submit"
                            disabled={submitting}
                            className="w-full bg-slate-900 hover:bg-slate-800 text-white"
                            data-testid="signup-submit-button"
                        >
                            {submitting ? "Creating…" : "Create my workspace"}
                            {!submitting && <ArrowRight size={16} className="ml-1.5" />}
                        </Button>
                        <p className="text-xs text-slate-500 text-center" data-testid="signup-consent-notice">
                            {CONSENT_NOTICE}
                        </p>
                    </form>

                    <div className="mt-6 pt-6 border-t border-slate-200 text-sm text-slate-600 space-y-1.5">
                        <p>
                            Been given a join code?{" "}
                            <Link to="/join" className="text-slate-900 font-medium underline">
                                Join your estate
                            </Link>
                        </p>
                        <p>
                            Already have an account?{" "}
                            <Link to="/login" className="text-slate-900 font-medium underline">
                                Sign in
                            </Link>
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
}
